/**
 * Auth hooks — TanStack Query wrappers around `AuthApi`.
 *
 * Per T3 ownership line: HTTP only. OAuth redirect / session bootstrap /
 * token storage are T7.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EmailLoginRequest,
  GoogleAuthRequest,
  RegisterRequest,
  RequestResetRequest,
  ResetRequest,
  SetPasswordRequest,
  VerifyRequest,
} from '../../types/user';
import { useApi } from './context';
import { keys } from './keys';

export interface UseMeOptions {
  /**
   * When `false`, the underlying query is disabled and /auth/me is not
   * fetched. T7's session bootstrap passes `enabled: tokens.getAccessToken()
   * !== null` so logged-out users never generate a guaranteed-401 on app
   * entry.
   */
  enabled?: boolean;
}

export function useMe(options: UseMeOptions = {}) {
  const api = useApi();
  return useQuery({
    queryKey: keys.auth.me,
    queryFn: () => api.auth.me(),
    enabled: options.enabled ?? true,
  });
}

export function useLoginWithGoogle() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GoogleAuthRequest) => api.auth.loginWithGoogle(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

/**
 * Email + password sign-in (T27.2.10.1). Mirrors `useLoginWithGoogle` — a plain
 * fetch mutation through `AuthApi.loginWithEmail`, platform-free (CP1-CP4). The
 * caller (LoginPage) applies the returned session + navigates.
 */
export function useLoginWithEmail() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmailLoginRequest) => api.auth.loginWithEmail(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

/**
 * Public registration (T48). A plain fetch mutation through `AuthApi.register`,
 * platform-free (CP1-CP4). Register now PROVISIONS the account and issues a
 * session, so on success the caller applies the session + navigates into the app
 * (no inbox interstitial). Invalidates `auth.me` like login. A 409 (existing
 * email) surfaces as an `ApiError` the caller branches on.
 */
export function useRegister() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterRequest) => api.auth.register(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

/**
 * Re-send the email-confirmation link for the signed-in account (T48). Drives
 * the in-app confirm-email nudge's "Resend".
 */
export function useResendVerification() {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.auth.resendVerification(),
  });
}

/**
 * Consume a verification token (T27.2.10.1 Phase B). On success a session is
 * issued; the caller applies it + navigates. Invalidates auth.me like login.
 */
export function useVerifyEmail() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VerifyRequest) => api.auth.verify(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

/** Request a password-reset email (T27.2.10.1 Phase B). Non-enumerating. */
export function useRequestReset() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: RequestResetRequest) => api.auth.requestReset(input),
  });
}

/**
 * Complete a password reset (T27.2.10.1 Phase B). Issues a fresh session; the
 * caller applies it + navigates. Invalidates auth.me.
 */
export function useResetPassword() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ResetRequest) => api.auth.reset(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

export function useLogout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      qc.clear();
    },
  });
}

/**
 * Explicit refresh mutation — rarely needed in UI (the HttpClient handles
 * 401-refresh automatically). Exposed for session-bootstrap flows in T7.
 */
export function useRefreshSession() {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.auth.refresh(),
  });
}

/**
 * The sign-in methods linked to the authenticated account (T27.2.10.1) — read
 * for the Settings link/unlink controls.
 */
export function useAuthIdentities(options: UseMeOptions = {}) {
  const api = useApi();
  return useQuery({
    queryKey: keys.auth.identities,
    queryFn: () => api.auth.identities(),
    enabled: options.enabled ?? true,
  });
}

/**
 * Set (or change) the password on the signed-in account (T27.2.10.1). On
 * success the identities query is invalidated so the Settings UI flips to the
 * linked state.
 */
export function useSetPassword() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetPasswordRequest) => api.auth.setPassword(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.identities });
    },
  });
}

/**
 * Unlink a sign-in method (T27.2.10.1). The server blocks removing the last
 * identity (409); the caller surfaces the reason. Invalidates the identities
 * query on success.
 */
export function useUnlinkIdentity() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: 'password' | 'google') =>
      api.auth.unlinkIdentity(provider),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.auth.identities });
    },
  });
}
